import { useCallback, useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { Toggle, inputCls, textareaCls, labelCls, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { slugify } from "@/lib/slugify"
import { LoadState } from "@/components/ui/LoadState"

type ProgramForm = {
  name: string
  slug: string
  short_description: string
  description: string
  color: string
  icon: string
  order_index: number
  is_published: boolean
}

const initialForm: ProgramForm = {
  name: "",
  slug: "",
  short_description: "",
  description: "",
  color: "",
  icon: "",
  order_index: 0,
  is_published: false,
}

export default function ProgramFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<ProgramForm>(initialForm)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const res = await api.get(`/academy/programs/admin/${id}`)
      const p = res.data?.data
      if (!p) {
        setError("Ruta académica no encontrada")
        return
      }
      setForm({
        name: p.name,
        slug: p.slug,
        short_description: p.shortDescription ?? "",
        description: p.description ?? "",
        color: p.color ?? "",
        icon: p.icon ?? "",
        order_index: Number(p.orderIndex ?? 0),
        is_published: p.isPublished,
      })
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  function goBack() {
    navigate(isEdit && id ? `/programs/programs/${id}` : "/programs/programs")
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSaving(true)
    setError("")

    try {
      if (isEdit && id) {
        await api.put(`/academy/programs/admin/${id}`, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          short_description: form.short_description.trim() || undefined,
          color: form.color.trim() || undefined,
          icon: form.icon.trim() || undefined,
          order_index: Number(form.order_index || 0),
          is_published: form.is_published,
        })
        navigate(`/programs/programs/${id}`)
      } else {
        const res = await api.post("/academy/programs/admin", {
          name: form.name.trim(),
          slug: slugify(form.name),
          description: form.description.trim() || undefined,
          short_description: form.short_description.trim() || undefined,
          color: form.color.trim() || undefined,
          icon: form.icon.trim() || undefined,
          order_index: Number(form.order_index || 0),
          is_published: form.is_published,
        })
        const newId = res.data?.data?.id
        qc.invalidateQueries({ queryKey: ["server-table", "/academy/programs/admin/all"] })
        navigate(newId ? `/programs/programs/${newId}` : "/programs/programs")
      }
    } catch (err) {
      setError(getErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Rutas académicas", to: "/programs/programs" },
        isEdit ? "Editar ruta" : "Nueva ruta",
      ]}
      title={isEdit ? "Editar ruta académica" : "Nueva ruta académica"}
      subtitle={isEdit ? "Actualiza los datos de la ruta" : "Crea una nueva ruta académica"}
      tabs={[]}
      metrics={[]}
    >
      {loading ? (
        <LoadState label="Cargando ruta..." />
      ) : (
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Identidad */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Identidad</h3>
              <div>
                <label className={labelCls}>Nombre *</label>
                <input
                  required
                  autoFocus
                  placeholder="Nombre de la ruta académica"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      name: e.target.value,
                      slug: isEdit ? f.slug : slugify(e.target.value),
                    }))
                  }
                  className={inputCls}
                />
              </div>
              <p className="rounded-lg bg-gray-50 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 dark:bg-gray-800">
                Slug: <span className="font-mono">{form.slug || "—"}</span> (autogenerado, no editable)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Color</label>
                  <input
                    placeholder="#1F3A8A"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Ícono</label>
                  <input
                    placeholder="rocket"
                    value={form.icon}
                    onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>

            {/* Descripción */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Descripción</h3>
              <textarea
                rows={2}
                placeholder="Descripción corta (aparece en el catálogo)"
                value={form.short_description}
                onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))}
                className={textareaCls}
              />
              <textarea
                rows={8}
                placeholder="Descripción completa (opcional)"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className={`${textareaCls} lg:min-h-[11rem]`}
              />
            </div>

            {/* Configuración */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Configuración</h3>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className={labelCls}>Orden de despliegue</label>
                  <input
                    type="number"
                    min={0}
                    value={form.order_index}
                    onChange={(e) => setForm((f) => ({ ...f, order_index: Number(e.target.value) }))}
                    className={inputCls}
                  />
                </div>
                <Toggle
                  checked={form.is_published}
                  onChange={(v) => setForm((f) => ({ ...f, is_published: v }))}
                  label="Publicado"
                  description="Visible en el catálogo público"
                />
              </div>
            </div>
          </div>

          {error ? (
            <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className={btnSecondary}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} className={btnSave}>
              {saving ? "Guardando..." : isEdit ? "Guardar" : "Crear ruta"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
